import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Smartphone, Tablet, Laptop, Gamepad2, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PriceListPage = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const response = await axios.get(`${API}/api/repair-services`);
                setServices(response.data);
            } catch {
                toast.error("Errore nel caricamento del listino");
            } finally {
                setLoading(false);
            }
        };
        fetchServices();
    }, []);

    const deviceTypes = [
        { id: 'smartphone', label: 'Smartphone', icon: Smartphone },
        { id: 'tablet', label: 'Tablet', icon: Tablet },
        { id: 'laptop', label: 'Computer/Laptop', icon: Laptop },
        { id: 'console', label: 'Console Gaming', icon: Gamepad2 },
    ];

    const getServicesByType = (type) => services.filter(s => s.device_type === type);

    if (loading) {
        return (
            <div className="min-h-screen py-8">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse space-y-8">
                        <div className="h-12 w-64 bg-surface rounded mx-auto" />
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="bg-surface rounded-xl h-64" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-3xl sm:text-4xl font-bold mb-4">Listino Prezzi</h1>
                    <p className="text-muted max-w-2xl mx-auto">
                        Prezzi trasparenti per tutti i nostri servizi di riparazione. 
                        Tutti gli interventi includono garanzia di 6 mesi.
                    </p>
                </div>

                {/* Price Tables */}
                <div className="space-y-8">
                    {deviceTypes.map((deviceType) => {
                        const typeServices = getServicesByType(deviceType.id);
                        if (typeServices.length === 0) return null;
                        
                        return (
                            <div 
                                key={deviceType.id} 
                                className="bg-surface border border-border rounded-xl overflow-hidden"
                                data-testid={`pricelist-${deviceType.id}`}
                            >
                                {/* Table Header */}
                                <div className="bg-surface-elevated px-6 py-4 flex items-center gap-3 border-b border-border">
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <deviceType.icon className="w-5 h-5 text-primary" />
                                    </div>
                                    <h2 className="text-xl font-semibold">{deviceType.label}</h2>
                                </div>
                                
                                {/* Table Content */}
                                <div className="divide-y divide-border">
                                    {typeServices.map((service) => (
                                        <div 
                                            key={service.id} 
                                            className="px-6 py-4 flex items-center justify-between hover:bg-surface-elevated/50 transition-colors"
                                        >
                                            <div className="flex-1">
                                                <h3 className="font-medium">{service.name}</h3>
                                                <p className="text-sm text-muted mt-1">{service.description}</p>
                                                <span className="text-xs text-muted">
                                                    Tempo stimato: ~{service.duration_minutes} minuti
                                                </span>
                                            </div>
                                            <div className="text-right ml-4">
                                                <span className="text-2xl font-bold text-primary">
                                                    €{service.price.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Notes */}
                <div className="mt-12 bg-surface border border-border rounded-xl p-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-primary" />
                        Note importanti
                    </h3>
                    <ul className="space-y-2 text-muted text-sm">
                        <li>• Tutti i prezzi sono IVA inclusa</li>
                        <li>• La diagnosi è sempre gratuita</li>
                        <li>• Garanzia di 6 mesi su tutte le riparazioni</li>
                        <li>• I tempi indicati sono stime e possono variare</li>
                        <li>• Per riparazioni non elencate, contattaci per un preventivo</li>
                        <li>• Utilizziamo solo ricambi originali o di alta qualità certificata</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default PriceListPage;
